<script setup>
    import { ref, reactive, computed, onMounted } from "vue";
    import { Loader } from "@googlemaps/js-api-loader";
    import { gCsvData } from "../../public/ressources/script/stores.js";
    import { dict as i18nDict } from "../../public/ressources/script/dict.js";

    // ----- Config init (reprend tes variables) -----
    const language = ref("fr"); // "fr" | "en"
    const initLatitude = 45.920587;
    const initLongitude = -72.729492;
    const initZoom = 7;
    const markerSrc = "/ressources/images/menu_images/signet2.png"; // adapte le chemin à ton projet
    const markerWidth = 34;
    const markerHeight = 49;

    const listIcon = [
        { img: "/_media/image/iconCarte1.png", link: "/reparation-pare-brise", title: "Réparation & remplacement de pare-brise" },
        { img: "/_media/image/iconCarte2.png", link: "/accessoires-auto/attaches-remorque", title: "Attaches de remorque" },
        { img: "/_media/image/iconCarte3.png", link: "/accessoires-auto/supports-auto-varies", title: "Supports variés" },
        { img: "/_media/image/iconCarte4.png", link: "/accessoires-auto/marquage-antivol", title: "Marquage antivol" },
        { img: "/_media/image/iconCarte5.png", link: "/accessoires-auto/demarreurs-a-distance", title: "Démarreurs à distance" },
        { img: "/_media/image/iconCarte6.png", link: "/accessoires-auto/sellettes", title: "Sellettes" },
        { img: "/_media/image/iconCarte7.png", link: "/accessoires-auto/couvre-caisses-repliables", title: "Couvre-caisses repliables" },
        { img: "/_media/image/iconCarte8.png", link: "/accessoires-auto/vitres-teintees", title: "Vitres teintées" },
    ];

    // ----- Refs DOM -----
    const mapEl = ref(null);
    const searchInputEl = ref(null);
    const directionsPanelEl = ref(null);

    // ----- Google objects -----
    let map, geocoder, infoWindow, directionsService, directionsRenderer, autocomplete;

    // ----- State -----
    const state = reactive({
        stores: [],
        filteredStores: [],
        selectedStoreId: null,
        userLocation: null, // {lat, lng}
        routeVisible: false,
        searchPlaceholder: "Inscrivez votre code postal",
        dict: computed(() => i18nDict[language.value]),
        noStoresMsg: computed(() =>
            state.filteredStores.length ? i18nDict[language.value].nostores : i18nDict[language.value].nostoresarea
        ),
    });

    // ----- Helpers -----
    function toStores(csv) {
        const arr = [];
        for (let i = 0; i < csv.length; i++) {
            const row = csv[i];
            const special = String(row[22] || "").toLowerCase() === "oui";
            arr.push({
            id: `store${i}`,
            position: { lat: Number(row[0]), lng: Number(row[1]) },
            city: row[2] || "",
            logo: row[3] || null,
            title: row[4] || "",
            addressLines: [row[5] || "", row[6] || "", row[7] || ""].filter(Boolean),
            phones: [row[8] || "", row[9] || "", row[10] || ""].filter(Boolean),
            link: row[11] || null,
            special,
            marker: null,
            });
        }
        return arr;
    }

    function buildInfoContent(s) {
        const logoHtml = s.special
            ? ""
            : s.logo
            ? `<div class="logo"><img src="${s.logo}" alt="${s.title}"/></div>`
            : "";
        const webHtml = s.link ? `<a href="${s.link}" class="plusinfos" target="_blank" rel="noreferrer">${state.dict.infos}</a>` : "";
        const phoneHtml = s.phones.length ? `<div class="phone">${s.phones.map(ph => `<div>${ph}</div>`).join("")}</div>` : "";

        return `
            <div class="store">
            <div class="city">${s.city}</div>
            ${logoHtml}
            <div class="titre">${s.title}</div>
            <div class="address">${s.addressLines.join("<br/>")}</div>
            ${phoneHtml}
            <div class="web">${webHtml}</div>
            </div>
        `;
    }

    function selectStore(id, pan = true) {
        const s = state.stores.find(x => x.id === id);
        if (!s) return;
        state.selectedStoreId = id;

        if (infoWindow) {
            infoWindow.setContent(buildInfoContent(s));
            if (s.marker) infoWindow.open({ map, anchor: s.marker });
            else { infoWindow.setPosition(s.position); infoWindow.open(map); }
        }
        if (pan) map.panTo(s.position);

        if (state.routeVisible && state.userLocation) routeTo(s);
    }

    function routeTo(s) {
        if (!directionsService || !directionsRenderer || !state.userLocation) return;
        directionsService.route(
            { origin: state.userLocation, destination: s.position, travelMode: google.maps.TravelMode.DRIVING },
            (res, status) => { if (status === "OK" && res) directionsRenderer.setDirections(res); }
        );
    }

    function toggleDirections(show) {
        state.routeVisible = show;
        if (!show) { directionsRenderer.setMap(null); return; }
        directionsRenderer.setMap(map);
        const s = state.stores.find(x => x.id === state.selectedStoreId);
        if (s && state.userLocation) routeTo(s);
    }

    function geocodeAndCenter(query) {
        if (!query || !query.trim()) return;
        geocoder.geocode({ address: `${query} Canada`, bounds: map.getBounds() || undefined }, (res, status) => {
            if (status === "OK" && res && res[0]) {
            const loc = res[0].geometry.location;
            const center = { lat: loc.lat(), lng: loc.lng() };
            state.userLocation = center;
            if (res[0].geometry.viewport) map.fitBounds(res[0].geometry.viewport);
            else { map.setCenter(center); map.setZoom(13); }
            filterAndSortByDistance();
            }
        });
    }

    function filterAndSortByDistance() {
        const stores = [...state.stores];
        if (state.userLocation) {
            const toRad = deg => (deg * Math.PI) / 180;
            const dist = (a, b) => {
            const R = 6371;
            const dLat = toRad(b.lat - a.lat);
            const dLng = toRad(b.lng - a.lng);
            const s1 = Math.sin(dLat/2) ** 2;
            const s2 = Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng/2) ** 2;
            return 2 * R * Math.atan2(Math.sqrt(s1 + s2), Math.sqrt(1 - s1 - s2));
            };
            stores.sort((a, b) => dist(state.userLocation, a.position) - dist(state.userLocation, b.position));
        }
        state.filteredStores = stores.slice(0, 15);
    }

    function printDirections() {
        const s = state.stores.find(x => x.id === state.selectedStoreId);
        if (!s || !state.userLocation) return;
        const url = new URL("https://www.google.com/maps/dir/");
        url.searchParams.set("api", "1");
        url.searchParams.set("origin", `${state.userLocation.lat},${state.userLocation.lng}`);
        url.searchParams.set("destination", `${s.position.lat},${s.position.lng}`);
        url.searchParams.set("hl", language.value);
        window.open(url.toString(), "_blank");
    }
    function loadGoogleMaps(apiKey) {
        // Script déjà chargé ?
        if (window.google?.maps?.importLibrary) return Promise.resolve();

        // Évite d'injecter deux fois le script
        if (document.getElementById("gmaps-script")) {
            return new Promise((resolve, reject) => {
            const check = setInterval(() => {
                if (window.google?.maps?.importLibrary) {
                clearInterval(check);
                resolve();
                }
            }, 50);
            setTimeout(() => {
                clearInterval(check);
                reject(new Error("Timeout chargement Google Maps"));
            }, 10000);
            });
        }

        return new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.id = "gmaps-script";
            s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=places`;
            s.async = true;
            s.defer = true;
            s.onload = () => resolve();
            s.onerror = () => reject(new Error("Échec du chargement de Google Maps"));
            document.head.appendChild(s);
        });
    }
    
        // ----- Mount -----
onMounted(async () => {
  
  const apiKey = "AIzaSyDDuaRF4lHcitMJRMRWN_Bm1qo1icwWetg";
  //const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
  await loadGoogleMaps(apiKey);
  await google.maps.importLibrary("places");
  // "maps" est déjà disponible via le script, mais tu peux aussi faire:
  // await google.maps.importLibrary("maps");

  // --- services Google Maps
  geocoder = new google.maps.Geocoder();
  infoWindow = new google.maps.InfoWindow();
  directionsService = new google.maps.DirectionsService();
  directionsRenderer = new google.maps.DirectionsRenderer({ draggable: false, panel: directionsPanelEl.value || undefined });

  // --- carte
  map = new google.maps.Map(mapEl.value, {
    center: { lat: initLatitude, lng: initLongitude },
    zoom: initZoom,
    mapTypeId: google.maps.MapTypeId.ROADMAP,
  });


  // --- Autocomplete (Places)
  if (searchInputEl.value) {

    autocomplete = new google.maps.places.Autocomplete(searchInputEl.value);
    autocomplete.bindTo("bounds", map);
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;
      const loc = place.geometry.location;
      const center = { lat: loc.lat(), lng: loc.lng() };
      state.userLocation = center;
      if (place.geometry.viewport) map.fitBounds(place.geometry.viewport);
      else { map.setCenter(center); map.setZoom(13); }
      filterAndSortByDistance();
    });
  }

  // --- données + marqueurs (inchangé)
  state.stores = toStores(gCsvData);
  filterAndSortByDistance();

  const icon = {
    url: markerSrc,
    size: new google.maps.Size(markerWidth, markerHeight),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(markerWidth / 2, markerHeight),
    scaledSize: new google.maps.Size(markerWidth, markerHeight),
  };

  state.stores.forEach((s) => {
    const marker = new google.maps.Marker({
      map,
      position: s.position,
      icon: s.special ? undefined : icon, // icône custom si pas "special"
      title: s.title,
    });
    s.marker = marker;
    marker.addListener("click", () => selectStore(s.id, false));
  });

  map.addListener("click", () => { infoWindow.close(); state.selectedStoreId = null; });

  if (navigator.geolocation) {

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        state.userLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        map.setCenter(state.userLocation);
        map.setZoom(11);
        filterAndSortByDistance();
      },
      () => {},
      { maximumAge: 60000, timeout: 10000 }
    );

  }
});

</script>

<template>
  <div class="store-locator">
    <div class="panel">
      <form class="storelocator-filter" @submit.prevent="geocodeAndCenter(searchInputEl?.value || '')">
        <div class="location-search">
          <h4>{{ state.dict.titre }}</h4>
          <input ref="searchInputEl" :placeholder="state.searchPlaceholder" />
        </div>
      </form>
      <ul class="store-list" >
        <li v-if="!state.filteredStores.length" class="no-stores">{{ state.noStoresMsg }}</li>
        <li v-for="s in state.filteredStores" :key="s.id"
            class="store"
            :class="{ highlighted: s.id === state.selectedStoreId }"
            @click="selectStore(s.id)">
          <div class="city">{{ s.city }}</div>
          <div class="logo" v-if="!s.special && s.logo"><img :src="s.logo" :alt="s.title" /></div>
          <div class="titre">{{ s.title }}</div>
          <div class="address" v-html="s.addressLines.join('<br/>')" />
          <div class="phone">
            <div v-for="(ph, i) in s.phones" :key="i">{{ ph }}</div>
          </div>
          <div class="web" v-if="s.link"><a :href="s.link" target="_blank" rel="noreferrer">{{ state.dict.infos }}</a></div>
          <img class="iconArrow" src="/ressources/images/menu_images/fleche-concessionaires.png" />
        </li>
      </ul>

      <div class="directions-panel">
        <div ref="directionsPanelEl" class="rendered-directions"></div>
      </div>
    </div>

    <div class="mapCanvas" ref="mapEl" />
  </div>
</template>

<style scoped>
    .store-locator { display: grid; grid-template-columns: 400px 1fr; gap: 16px; max-height: 400px; }
    .panel { overflow: auto; max-height: 400px; }
    .mapCanvas { width: 100%; height: 400px; min-height: 400px; border-radius: 12px; }
    .store-list { list-style: none; padding: 0; margin: 12px 0; }
    .store { padding: 10px; border-bottom: 1px solid #eee; cursor: pointer; position:relative;}
    .store .iconArrow { transition: transform 1s ease; }
    .iconArrow { position:absolute; top:40%; right:10%; }
    .store:hover .iconArrow { transform: scale(1.5); }
    .store.highlighted { background: #f6f8ff; }
    .logo img { max-width: 140px; max-height: 60px; object-fit: contain; }
    .actions { display: flex; gap: 8px; margin: 8px 0; }
    .location-search input { width: 100%; padding: 8px; }
    .directions-panel { margin-top: 8px; }
    .plusinfos { text-decoration: underline; }
    .location-search { position: relative; padding: 1em 5% 1.5em 7%; border-bottom-width: 1px; border-bottom-style: solid; border-bottom-color: #e3681c; background-color: #000; background-image: none; }
    .location-search H4 { padding-bottom: 0px; line-height: 24px; margin-top: 0px; margin-right: 0px; margin-bottom: 12px; margin-left: 8px; padding-left: 0px; padding-right: 0px; display: inline; color: #fff; padding-top: 0px; font: 400 1.8571em/1.1em 'calibri', Arial, Helvetica, sans-serif; }
    .location-search INPUT { margin-top: 12px; margin-right: 0px; margin-bottom: 0px; margin-left: 0px; padding-top: 0px; padding-right: 4%; padding-bottom: 0px; padding-left: 4%; width: 91%; height: 33px; line-height: 33px; background-color: #fff; font-size: 1em; color: #000; border: 1px #000 solid; }
    /*A mettre dans le class dessous background-image: url(/ressources/images/menu_images/degrade-pied-accueil.jpg);*/
    .location-search INPUT.btnsubmit { background-color: transparent; background-repeat: no-repeat; background-attachment: scroll; background-position: top left; width: 34px; height: 22px; position: absolute; left: 190px; top: 0px; }
    .concession .voir a, .store-list LI.store, .menuCss1 ul li a, .menuCss2 ul li a, .GpcResultPager a, .GpcDisplayOption, .GpcMenu li a, .GpcMenu li ul li { -webkit-transition: all 200ms ease-in-out; -moz-transition: all 200ms ease-in-out; -ms-transition: all 200ms ease-in-out; -o-transition: all 200ms ease-in-out; transition: all 200ms ease-in-out; }
    UL.store-list { padding-bottom: 0px; margin-top: 0px; margin-right: 0px; margin-bottom: 0px; margin-left: 0px; padding-left: 0px; padding-right: 0px; padding-top: 0px; height: 337px; overflow: auto; }
    .store-list LI.store:first-child { border-top-width: 0px; border-top-style: none; border-top-color: -moz-initial; border-top-color: initial; }
    .store-list LI.store { padding-bottom: 0.7em; list-style-type: none; margin-top: 0px; margin-right: 0px; margin-bottom: 0px; margin-left: 0px; padding-left: 5%; padding-right: 5%; list-style-image: none; padding-top: 0.7em; border-top-width: 1px; border-top-style: solid; border-top-color: #cfcfcf; border-right-width: 1px; border-right-style: solid; border-right-color: #cfcfcf; }
    .no-stores { list-style-type: none; margin-bottom: 4px; margin-left: 8px; font-size: 12px; font-weight: bold; list-style-image: none; }
    .directions-panel { position: relative; width: 100%; left: 0px; }
    .directions-panel INPUT[type=submit] { display: none; }
    .contenu h2 { font: 700 2.1428em/1.2em 'calibri', Arial, Helvetica, sans-serif; color: #f37121; margin: 0.9em 0px 0.3em 0px; }
    .contenu h2 a { color: #f37121; }
    .panel { position: relative; width: auto; float: left; height: 448px; z-index: 1; }
    .feature { display: none; }
    .mapCanvas { width: 100%; float: left; height: 448px; }
    .mapCanvas .store { padding-bottom: 20px; width: 215px; }
    .gm-style-iw > div{ overflow:hidden!important; min-height:300px; height: 300px !important; }
.gm-style .gm-style-iw{
padding-right:15px;
/*top:initial!important;*/
bottom:0px;
min-height:300px;
height: 300px !important;

}
.store {
  color: #000;
  font-family: Arial, Helvetica, sans-serif !important;
}

.panel .store .city {
  font: 400 1.4285em/1.3em 'calibri', Arial, Helvetica, sans-serif;
  margin-bottom: 5px;
  color: #000;
}

.panel .store .logo {
  display: none;
}

.panel .store .titre {
  font: 400 1em/1.5em Arial, Helvetica, sans-serif;
  color: #656565;
  white-space: nowrap;
}

.panel .store .address, .panel .store .special {
  display: none;
}
.panel .store .phone {
  display: block;
}

.panel .store .phone div:first-child {
  padding-top:10px;
  display: block !important;
  font: 400 1em/1.5em Arial, Helvetica, sans-serif;
  color: #000;
}

  .panel .store .phone div {
    display: none;
  }

.panel .store .web {
  display: none;
}

.panel .store .misc {
  display: none;
}

.mapPanel .store .plusinfos {
  z-index: 3000;
  position: absolute;
  line-height: initial;
  bottom: 0px;
  background: none;
  height: 100%;
  width: 100%;
  display: inline-block;
  cursor: pointer;
  right: 0px;
}

.mapCanvas .store .city {
  display: none;
}

.mapCanvas .store .logo {
}

.mapCanvas .store .titre {
  font: 400 1.4285em/1.3em 'calibri', Arial, Helvetica, sans-serif;
  margin: 10px 0px;
}

.mapCanvas .store .address {
  font: 400 1em/1.5em Arial, Helvetica, sans-serif;
  color: #000;
  margin-bottom: 10px;
}
.mapCanvas .store .special{display:none !important;}

.mapCanvas .store .phone {
  font: 400 1em/1.5em Arial, Helvetica, sans-serif;
  color: #000;
}

.mapCanvas .store .web {
}

.mapCanvas .store .misc {
  display: none;
}

.mapCanvas .store .plusinfos {
  z-index: 3000;
  position: absolute;
  /*bottom: -8px;*/
bottom:0px;
  /*background-image: url(../../../_assets/images/menu_images/plus-map.png);*/
  background-repeat: repeat;
  background-attachment: scroll;
  background-position: top left;
  height: 50px;
  width: 50px;
  display: inline-block;
  cursor: pointer;
  /*right: -31px;*/
right:1px;
}

.store .features {
  display: none;
}

.store A {
  color: #1600dc;
  text-decoration: underline;
}

  .store a:hover {
    color: #1600dc !important;
    text-decoration: none;
  }

.store .web IMG {
  margin-right: 6px;
}

DIV.store .web .title {
  margin-bottom: 4px;
  margin-top: 16px;
}
</style>
